import DefaultPageLoader from 'common/components/DefaultPageLoader';
import PageHeader from 'common/components/PageHeader';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import apps from 'common/services/apps';
import developerRequests from 'common/services/developerRequests';
import { DeveloperRequestPayload } from 'common/types/developerRequests';
import React from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import RequestForm from './RequestForm';

export function ApiPlatformRequests() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appClientId } = params;

  // The app-details page passes `approved=1` when launching the form for an
  // already-approved app, so the form hides the "App approval" request type.
  const appApproved = searchParams.get('approved') === '1';

  // The tier dropdown can only offer the *next* band, so the form needs the app's
  // current member cap. Fetched here rather than passed through the URL, which a
  // developer could edit to unlock a higher band than they are entitled to.
  const getAppTask = useAsync(
    async () => (appClientId ? apps.getApp(appClientId) : undefined),
    [appClientId],
  );

  const submitTask = useAsyncCallback(
    async (payload: DeveloperRequestPayload) => {
      await developerRequests.submitDeveloperRequest(payload);
    },
  );

  // Surface a friendly, retry-able message when the submission fails. Prefer a
  // usable message from the API; otherwise fall back to a generic one. The form
  // stays on the submit step so the user can retry.
  const submitError = submitTask.error
    ? submitTask.error.message ||
      'Something went wrong submitting your request. Please try again.'
    : undefined;

  const goBackToApp = () => {
    if (appClientId) {
      navigate(`/apps/${appClientId}`);
    } else {
      navigate('/');
    }
  };

  const onBackFromStart = () => {
    navigate(-1);
  };

  // Wait for the app before rendering: without its cap we cannot tell the
  // developer which tier they are eligible for. A failed lookup is not fatal —
  // the form falls back to hiding the member-limit question.
  if (appClientId && getAppTask.loading) {
    return (
      <>
        <PageHeader title="WHOOP API Platform Requests" />
        <DefaultPageLoader />
      </>
    );
  }

  return (
    <>
      <PageHeader title="WHOOP API Platform Requests" />
      <RequestForm
        appClientId={appClientId}
        appApproved={appApproved}
        currentUserLimit={getAppTask.result?.adoptedUserLimit}
        currentUserCount={getAppTask.result?.adoptedUserCount}
        currentMinuteLimit={getAppTask.result?.minuteRequestLimit}
        currentDayLimit={getAppTask.result?.dayRequestLimit}
        loading={submitTask.loading}
        error={submitError}
        submitted={submitTask.status === 'success'}
        onSubmit={submitTask.execute}
        onCancel={goBackToApp}
        onBackFromStart={onBackFromStart}
      />
    </>
  );
}

export default () => (
  <AuthenticatedLayout pageIdentifier="api-platform-requests">
    <ApiPlatformRequests />
  </AuthenticatedLayout>
);
